<template>
  <div class="relative h-full w-full">
    <resize-observer @notify="handleResize" />
    <svg class="w-full h-full sm:w-96 sm:h-96 object-contain">
      <!-- State Border -->
      <path
          :stroke-width="stateBorderWidth"
          :d="geoPath(mesh)"
      />

      <!-- All counties merged -->
      <path
          :d="geoPath(features)"
          :stroke-width="countyBorderWidth"
          fill="none"
          class="cursor-pointer"
      />

      <!-- Active / Highlighted Counties -->
      <path
          v-for="(item, index) in matchedCounties.features"
          v-on:click="countyClickHandler(item)"
          :key="index"
          :stroke-width="countyBorderWidth"
          class="cursor-pointer"
          :class="getCountyClass(item.properties.name)"
          :d="geoPath(item)"
      >
        <title>{{ item.properties.name }}</title>
      </path>
    </svg>
  </div>
</template>

<script>

import * as d3 from "d3";
import { mesh, feature } from "topojson-client";

export default {
  name: "CountyMap",
  props: {
    /**
     * Counties to be active
     * @type {string[]}
     */
    counties: {
      type: Array,
      default: () => [],
    },

    /**
     * Counties to be highlighted
     * @type {string[]}
     */
    highlightedCounty: {
      type: String,
      default: null,
    },

    /**
     * State name or abbreviation
     * @type {string}
     */
    state: {
      type: String,
      default: "Iowa",
      validator(value) {
        return true;
      },
    },

    /**
     * State Border
     * @type {number}
     */
    stateBorderWidth: {
      type: Number,
      default: 3,
    },

    /**
     * County Border
     * @type {number}
     */
    countyBorderWidth: {
      type: Number,
      default: 1,
    },
    /**
     * Handles user clicking a different county on the map
     * @type {function}
     */
    countyClickHandler:{
      type: Function,
      default: () => {}
    }
  },
  data: () => ({
    /** @type {Readonly<Geo.RootObject>} */
    usAsync: null,

    width: 200,
    height: 300
  }),
  computed: {
    /**
     * The target state object
     * @returns {Geo.Geometry2}
     */
    usState() {
      if(!this.usAsync) return null;
      return this.usAsync.objects.states.geometries.find((s) => {
        const {
          properties: { name },
        } = s;

        return name.toLowerCase() === this.state.toLowerCase();
      });
    },

    /**
     * Topography that is filtered to only contain data for selected state
     */
    filteredUs() {
      /** @type {Geo.Objects} */
      const { counties, nation, states } = this.usAsync.objects;

      return {
        ...this.usAsync,
        objects: {
          counties: {
            ...counties,
            geometries: counties.geometries.filter((c) => {
              const id = c.id.slice(0, 2);

              return id === this.usState.id;
            }),
          },
          nation,
          states,
        },
      };
    },

    /**
     * Main projection method to translate coordinates
     * @returns {d3.GeoIdentityTransform}
     */
    projection() {
      // const { x, y } = this.translate

      //  add extent based on bounds maybe

      const p = d3.geoIdentity();


      p.fitSize([this.width, this.height], this.features);

      // p.fitExtent([[20, 20], [960, 640]], this.us)

      return p;
    },

    /** @returns {d3.GeoPath} */
    geoPath() {
      return d3.geoPath().projection(this.projection);
    },

    /** @returns {d3.ExtendedGeometryCollection} */
    mesh() {
      if (!this.usAsync) return [];
      // return mesh(this.us, this.us.objects.states, (a, b) => a !== b)
      return mesh(this.usAsync, this.usState);
    },

    /** @returns {d3.ExtendedFeature} */
    features() {
      if (!this.usAsync) return [];
      return feature(this.usAsync, this.filteredUs.objects.counties);
    },

    /**
     * Contains renderable data for active counties
     * @returns {d3.ExtendedFeatureCollection}
     */
    matchedCounties() {

      if (!this.features.features) return {};
      const features = this.features.features.filter((g) => {
        const name = g.properties.name.toLowerCase();

        return !!this.counties.find(c => c.toLowerCase() === name);
      });

      return {
        type: this.features.type,
        features,
      };
    },
  },
  async mounted() {
    this.updateDimensions();
    /** @type {{default: Geo.RootObject}} */
    let data = await import("us-atlas/counties-albers-10m.json");
    this.usAsync = Object.freeze(data.default);
  },
  methods: {
    handleResize() {
      this.updateDimensions();
    },
    updateDimensions() {
      const { width, height } = this.$el.getBoundingClientRect();

      this.width = width;
      this.height = height;
    },
    getCountyClass(name) {
      return {
        highlight: this.highlightedCounty && this.highlightedCounty.toLowerCase() === name.toLowerCase()
      };
    }
  },
};
</script>

<style scoped>
  svg {
    overflow: visible;
  }
  path {
    fill: rgba(64, 84, 220, 1.0);
    stroke: rgba(245, 245, 245, 0.5);
  }
  .state-path {
      fill: none;
      stroke: rgba(245, 245, 245, 0.5);
  }

  .active {
    fill: rgba(64, 84, 220, 1.0);
    transition: fill 1s ease;
  }
  active:hover {
    fill: yellow;
    transition-duration: 0ms;
  }

  .highlight {
    fill: rgba(227, 187, 101, 1.0);
    transition-duration: 0ms;
  }
</style>